import { TIMESHEET_STATUSES, type TimesheetStatus } from '@clockwise/clients/timesheets';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { SearchInput } from './search-input';
import { STATUS_STYLES } from './timesheet-week-status-strip';

export interface TimesheetWeekFindBarProps {
  /** Per-status entry count shown next to each status dot. */
  statusCounts: Record<TimesheetStatus, number>;
  /** Selected status scope — empty means "all statuses". */
  selectedStatuses: ReadonlySet<TimesheetStatus>;
  onToggleStatus: (status: TimesheetStatus) => void;
  /** Free-text query (customer OR activity name). */
  query: string;
  onQueryChange: (q: string) => void;
  /** Whether a scope is active (query or status selected) — gates the stepper. */
  active: boolean;
  matchCount: number;
  /** 0-based index of the open match (shown as `currentIndex + 1`). */
  currentIndex: number;
  onPrev: () => void;
  onNext: () => void;
  /** Collapse the whole bar back to the search icon. */
  onClose: () => void;
}

/**
 * The expanded week-view find bar: one inline row with a customer/activity text
 * field, the status dot + count scope selector, a match stepper, and a close
 * button. A match is a non-empty cell satisfying both the text and status
 * halves; the stepper (Enter / Shift+Enter from the field, or the arrows) steps
 * matches and the page opens each match's cell popover. Rendered only while the
 * find is open — the toolbar shows a lone search icon otherwise. Everything is
 * toolbar-height and inline so opening it never changes the row height.
 */
export function TimesheetWeekFindBar({
  statusCounts,
  selectedStatuses,
  onToggleStatus,
  query,
  onQueryChange,
  active,
  matchCount,
  currentIndex,
  onPrev,
  onNext,
  onClose,
}: TimesheetWeekFindBarProps) {
  const noMatches = matchCount === 0;

  return (
    <TooltipProvider delayDuration={200}>
      {/* Native <search> landmark, inline (no bordered panel) so it sits at the
          toolbar's own height. Escape collapses the bar. */}
      <search
        aria-label="Find timesheet entries"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
        className="inline-flex items-center gap-2"
      >
        <SearchInput
          value={query}
          onValueChange={onQueryChange}
          placeholder="Find customer or activity…"
          className="w-44"
          inputClassName="h-8"
          autoFocus
          aria-label="Find customer or activity"
          onKeyDown={(e) => {
            // Enter / Shift+Enter step matches like browser find.
            if (e.key === 'Enter') {
              e.preventDefault();
              if (e.shiftKey) onPrev();
              else onNext();
            }
          }}
        />

        {/* Status dot + count scope selector; click a dot to narrow. A status
            with no entries this week is hidden (nothing to filter) — unless it's
            the currently-selected scope, so it stays available to deselect. */}
        <div className="inline-flex items-center gap-1">
          {TIMESHEET_STATUSES.map((status) => {
            const selected = selectedStatuses.has(status);
            const style = STATUS_STYLES[status];
            const count = statusCounts[status] ?? 0;
            if (count === 0 && !selected) return null;
            return (
              <Tooltip key={status}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`${style.label}: ${count}`}
                    aria-pressed={selected}
                    onClick={() => onToggleStatus(status)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs tabular-nums outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/40',
                      selected
                        ? 'bg-primary/10 ring-1 ring-primary'
                        : 'text-muted-foreground hover:bg-muted',
                    )}
                  >
                    <span className={cn('inline-block size-2.5 rounded-full', style.dot)} />
                    {count}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {style.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Stepper — only once a scope is active, so it doesn't sit empty. The
            prev/next arrows are a tight pair (narrowed, no gap) so they read as
            one control. */}
        {active ? (
          <div className="inline-flex items-center gap-1">
            <span
              className="min-w-[3.5rem] text-center text-xs tabular-nums text-muted-foreground"
              aria-live="polite"
            >
              {noMatches ? 'No matches' : `${currentIndex + 1} of ${matchCount}`}
            </span>
            <div className="inline-flex items-center">
              <Button
                size="icon-sm"
                variant="ghost"
                className="w-6"
                aria-label="Previous match"
                disabled={noMatches}
                onClick={onPrev}
              >
                <ChevronUp />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                className="w-6"
                aria-label="Next match"
                disabled={noMatches}
                onClick={onNext}
              >
                <ChevronDown />
              </Button>
            </div>
          </div>
        ) : null}

        <Button size="icon-sm" variant="ghost" aria-label="Close find" onClick={onClose}>
          <X />
        </Button>
      </search>
    </TooltipProvider>
  );
}
